import { Clock, Minus, TrendingDown, TrendingUp, type LucideIcon } from 'lucide-react';

import { ExameResponseOrigem, ExameResponseStatus } from '@/api/generated/model';

/**
 * Rótulos do exame.
 *
 * Status e origem aparecem na lista, no filtro e no formulário — escritos
 * diferente em cada um seria ruído numa tela que se lê em diagonal.
 */

export const STATUS = Object.values(ExameResponseStatus);

export const ROTULO_STATUS: Record<string, string> = {
  SOLICITADO: 'Solicitado',
  AMOSTRA_RECEBIDA: 'Amostra recebida',
  EM_ANALISE: 'Em análise',
  CONCLUIDO: 'Concluído',
  CANCELADO: 'Cancelado',
};

export const CLASSE_STATUS: Record<string, string> = {
  SOLICITADO: '',
  AMOSTRA_RECEBIDA: '',
  EM_ANALISE: 'text-amber-600 border-amber-600/40',
  CONCLUIDO: 'text-emerald-600 border-emerald-600/40',
  CANCELADO: 'text-destructive border-destructive/40',
};

export const ORIGENS = Object.values(ExameResponseOrigem);

export const ROTULO_ORIGEM: Record<string, string> = {
  VERTIS: 'Laboratório',
  /** Veio pronto de fora e vive como documento anexado — não tem analito. */
};

/**
 * Origens que aparecem no filtro da listagem — só a que tem nome próprio
 * (Laboratório). MANUAL e EXTERNO ficam de fora do filtro: são exceções que
 * aparecem quando existem, não uma categoria que alguém procura.
 */
export const ORIGENS_FILTRAVEIS = Object.keys(ROTULO_ORIGEM);

/**
 * Rótulo de exibição da origem. `ROTULO_ORIGEM` só cobre o caso que precisa
 * de um nome próprio (VERTIS → Laboratório) — as demais origens (MANUAL,
 * EXTERNO) não ganham entrada fixa ali, só a capitalização do próprio enum.
 */
export function rotuloDeOrigem(origem: string): string {
  return ROTULO_ORIGEM[origem] ?? origem.charAt(0) + origem.slice(1).toLowerCase();
}

/**
 * A interpretação é **derivada no servidor** pela faixa de referência da
 * espécie, sexo e idade do paciente, e persistida junto do resultado.
 *
 * A V1 recalculava isso na tela a cada render, o que dava divergência entre o
 * que aparecia e o que estava gravado. Aqui só se exibe o que veio.
 */
export const ROTULO_INTERPRETACAO: Record<string, string> = {
  NORMAL: 'Normal',
  ABAIXO: 'Abaixo',
  ACIMA: 'Acima',
  INDETERMINADO: 'Indeterminado',
};

export const CLASSE_INTERPRETACAO: Record<string, string> = {
  NORMAL: 'text-emerald-600 border-emerald-600/40',
  ABAIXO: 'text-amber-600 border-amber-600/40',
  ACIMA: 'text-amber-600 border-amber-600/40',
  INDETERMINADO: '',
};

/**
 * Ícone e cor de cada interpretação, no laudo.
 *
 * A V1 sinalizava assim (`Resultados.tsx:67-72`) e o vocabulário pegou: seta
 * para baixo/cima em vez de só a palavra. Mantido para quem vem da V1 não
 * precisar reaprender a ler o laudo.
 */
export const ICONE_INTERPRETACAO: Record<string, { icone: LucideIcon; classe: string }> = {
  NORMAL: { icone: Minus, classe: 'text-emerald-600 bg-emerald-600/10' },
  ABAIXO: { icone: TrendingDown, classe: 'text-blue-600 bg-blue-600/10' },
  ACIMA: { icone: TrendingUp, classe: 'text-red-600 bg-red-600/10' },
  INDETERMINADO: { icone: Clock, classe: 'text-muted-foreground bg-muted' },
};

/**
 * Data de CALENDÁRIO — coleta, liberação — formatada em **UTC**.
 *
 * ⚠️ Não é preciosismo: o formulário grava o que a pessoa escolheu como
 * `AAAA-MM-DDT00:00:00.000Z` (ver `dataParaISO`), e ler isso no fuso local
 * devolve o DIA ANTERIOR em qualquer fuso a oeste de Greenwich. Em São Paulo,
 * quem digita 01/08 na coleta lê 31/07 no laudo — num documento clínico, é
 * erro de dado, não detalhe de exibição.
 *
 * O par com `isoParaData`, que também corta em UTC, é o que fecha o
 * ida-e-volta: o que se digita é o que se lê.
 */
export function formatarData(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '—';
}

/**
 * Instante — `criadoEm`, `atualizadoEm` — no fuso de quem lê.
 *
 * Aqui o local está certo: é o momento em que algo aconteceu no servidor, e a
 * pessoa quer saber que horas eram para ela.
 */
export function formatarInstante(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';
}

/**
 * Data **com hora**, para o corte do sync.
 *
 * ⚠️ Só a data seria ambígua e, perto da meia-noite, errada por um dia: o corte
 * é um instante (o cadastro do veterinário) comparado em UTC no servidor, e um
 * `00:00Z` vira `30/06 21:00` em São Paulo. Mostrando a hora, quem lê consegue
 * decidir se o exame daquele dia entra ou não — com a data seca, não consegue.
 */
export function formatarDataHora(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

/**
 * `<input type="date">` → ISO com `Z`, e de volta.
 *
 * ⚠️ O corte é em UTC. O backend valida com `z.iso.datetime()`, que exige o
 * sufixo `Z` e rejeita offset (`-03:00` responde 422).
 */
export function dataParaISO(data: string): string {
  return `${data}T00:00:00.000Z`;
}

export function isoParaData(iso: string | null): string {
  return iso ? iso.slice(0, 10) : '';
}
